import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Users, User } from 'lucide-react';
import {
  buildItemsList,
  buildAccountGroupIndex,
  filterItems,
  buildSelectionParams,
  parseQuery,
  resolveTypedSelection,
} from '../lib/accountSelection';
import { loadPortfolioOptions } from '../lib/portfolioOptions';
import { getLastAccessedPortfolio } from '../../portfolio/lib/lastAccessedPortfolio';

/**
 * Shared portfolio/group selector used by every report popup.
 *
 * Distinguishes Groups (leading "@") from individual Accounts, routes by the
 * item's `type` (never the "@" heuristic), supports "@@" aggregate mode, and the
 * "type a name with no matching group -> list the groups it belongs to" fallback.
 * See docs/superpowers/specs/2026-06-24-report-groups-design.md.
 *
 * Emits the current selection via `onChange({ selectedItem, params })`, where
 * `params` carries group_codename + group_mode (or account_number + optional
 * specific_account) plus the display `portfolio` string — ready to merge into a
 * /fetch-data request. `params` is {} when nothing is selected.
 *
 * Two PM conveniences (docs/superpowers/specs/2026-06-29-report-shortname-and-autofill-design.md):
 *  - Press Enter to resolve the typed shortname/account and generate via the
 *    optional `onSubmit(selection)` callback. A typed shortname for a client with
 *    several accounts opens the dropdown instead (never silently picks one).
 *  - When no explicit `prefill` is given, autofill the last client the PM viewed
 *    on the detail page (session-scoped) so they can press Enter immediately.
 *
 * `initialSelection` restores a SPECIFIC prior pick (the report window's rerun
 * dialog - see lib/reportPrefill.js). Unlike `prefill`, which only seeds the
 * text and leaves the field unresolved, it commits a real selection - group
 * mode and specific-account narrowing included - so the dialog opens valid and
 * one click regenerates.
 */
const PortfolioSelector = ({
  darkMode = false,
  label = 'Portfolio',
  prefill = '',
  initialSelection = null,
  disabled = false,
  onChange,
  onValidityChange,
  onSubmit,
}) => {
  const [portfolio, setPortfolio] = useState(prefill || '');
  const [selectedItem, setSelectedItem] = useState(null);
  const [groupMode, setGroupMode] = useState('sequential'); // 'sequential' | 'aggregate'
  const [specificAccount, setSpecificAccount] = useState('ALL');
  const [showDropdown, setShowDropdown] = useState(false);
  const [data, setData] = useState({ groups: [], families: [], composites: [], accounts: [] });
  const [acctGroupIndex, setAcctGroupIndex] = useState(new Map());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const restoredRef = useRef(false);
  const autofilledRef = useRef(false);
  const autofocusedRef = useRef(false);
  const suppressFocusOpenRef = useRef(false);

  /** Build the { selectedItem, params } object emitted to the parent. */
  const buildSelection = useCallback((item, mode, specific) => {
    const params = item
      ? { ...buildSelectionParams(item, { mode, specificAccount: specific }), portfolio: item.type === 'group' ? `${mode === 'aggregate' ? '@@' : '@'}${item.codename}` : item.displayName }
      : {};
    return { selectedItem: item, params };
  }, []);

  const emit = useCallback(
    (item, mode, specific) => {
      const sel = buildSelection(item, mode, specific);
      onChange?.(sel);
      onValidityChange?.(!!item);
      return sel;
    },
    [buildSelection, onChange, onValidityChange],
  );

  /** Commit an item to the field's state (input text, selection, dropdown) and emit it. */
  const applySelection = useCallback((item, mode) => {
    const m = mode || 'sequential';
    setSelectedItem(item);
    setSpecificAccount('ALL');
    setShowDropdown(false);
    if (item.type === 'group') {
      setGroupMode(m);
      setPortfolio(`${m === 'aggregate' ? '@@' : '@'}${item.codename}`);
      return emit(item, m, 'ALL');
    }
    setPortfolio(item.displayName);
    return emit(item, m, 'ALL');
  }, [emit]);

  const fetchCodenames = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Shared, login-preloaded cache: the dropdown opens instantly with options
      // (incl. market values) that were warmed at login, and the ~15 popups reuse
      // one payload instead of each re-fetching /codenames.
      const d = await loadPortfolioOptions();
      setData(d);
      setAcctGroupIndex(buildAccountGroupIndex(d));
      setItems(buildItemsList(d));
    } catch (e) {
      setError('Failed to load portfolio options');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCodenames(); }, [fetchCodenames]);

  // Restore an exact prior selection once options load (the rerun dialog). This
  // matches by IDENTITY - the group's codename, the account's number - not by
  // the fuzzy text resolver, so a rerun can never land on a different portfolio
  // than the report it was launched from. A codename that no longer exists
  // (group deleted, account closed) simply leaves the field empty rather than
  // guessing a neighbour.
  useEffect(() => {
    if (restoredRef.current) return;
    if (!initialSelection) return;
    const loaded = (data.accounts && data.accounts.length)
      || (data.groups && data.groups.length)
      || (data.families && data.families.length)
      || (data.composites && data.composites.length);
    if (!loaded) return; // wait for options
    restoredRef.current = true;
    const items = buildItemsList(data);
    let item = null;
    if (initialSelection.group_codename) {
      item = items.find((i) => i.type === 'group' && i.codename === initialSelection.group_codename);
    } else if (initialSelection.account_number) {
      item = items.find((i) => i.type === 'account' && i.account_number === initialSelection.account_number);
    } else if (initialSelection.client_codename) {
      // No account number stored (legacy / chat-handoff params): fall back to
      // the shared resolver, and leave a multi-account codename unresolved.
      const { item: byName, ambiguous } = resolveTypedSelection(data, initialSelection.client_codename, acctGroupIndex);
      if (!ambiguous) item = byName;
    }
    if (!item) return;
    const isGroup = item.type === 'group';
    const mode = isGroup && initialSelection.group_mode === 'aggregate' ? 'aggregate' : 'sequential';
    const specific = (isGroup && (item.members || []).includes(initialSelection.specific_account))
      ? initialSelection.specific_account
      : 'ALL';
    setSelectedItem(item);
    setGroupMode(mode);
    setSpecificAccount(specific);
    setShowDropdown(false);
    setPortfolio(isGroup ? `${mode === 'aggregate' ? '@@' : '@'}${item.codename}` : item.displayName);
    emit(item, mode, specific);
  }, [data, acctGroupIndex, initialSelection, emit]);

  // Autofill the last client the PM viewed (session-scoped) once options load,
  // unless an explicit prefill (client-context navigation) or a restored
  // selection (rerun) is present. A single-account codename pre-selects so
  // Enter/Generate work immediately; a multi-account codename drops in as text
  // (the PM presses Enter -> dropdown); an unresolved codename leaves the field
  // empty.
  useEffect(() => {
    if (autofilledRef.current) return;
    if (prefill) return;
    if (initialSelection) return;
    if (selectedItem) return;
    if (!data.accounts || data.accounts.length === 0) return; // wait for options
    autofilledRef.current = true;
    const last = getLastAccessedPortfolio();
    if (!last || !last.codename) return;
    const { item, ambiguous } = resolveTypedSelection(data, last.codename, acctGroupIndex);
    if (item && !ambiguous) {
      applySelection(item, undefined);
    } else if (ambiguous) {
      setPortfolio(last.codename);
    }
  }, [data, acctGroupIndex, prefill, initialSelection, selectedItem, applySelection]);

  // Move focus into the popup once options load. On open, focus is still on the
  // report button that opened the dialog (outside the modal), so Enter would do
  // nothing until the PM clicked inside. Focusing the search box pulls focus into
  // the modal so Enter generates immediately; we suppress the open-on-focus
  // dropdown for this programmatic focus so the field isn't buried under the
  // full account list. (Focusing the disabled input mid-load would just blur, so
  // we wait until loading finishes.)
  useEffect(() => {
    if (autofocusedRef.current || loading || !inputRef.current) return;
    const loaded = (data.accounts && data.accounts.length) || (data.groups && data.groups.length) || error;
    if (!loaded) return;
    autofocusedRef.current = true;
    suppressFocusOpenRef.current = true;
    inputRef.current.focus({ preventScroll: true });
    suppressFocusOpenRef.current = false;
  }, [loading, data, error]);

  // Bind to the ownerDocument, not the global `document`: the selector also
  // renders inside a report window's own OS popup (the rerun dialog), whose
  // clicks never reach the opener's document, so the dropdown would never
  // close on an outside click there.
  useEffect(() => {
    if (!showDropdown) return undefined;
    const doc = dropdownRef.current?.ownerDocument || document;
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    doc.addEventListener('mousedown', onDocClick);
    return () => doc.removeEventListener('mousedown', onDocClick);
  }, [showDropdown]);

  // Enter ANYWHERE in the open popup generates, using the current selection — so
  // a PM who opened the popup with an autofilled portfolio (focus on the body) or
  // tabbed to the date field can press Enter without clicking back into the search
  // box. Active only while a selection exists. The search input's own onKeyDown
  // still resolves freshly typed text; we skip the input and native controls
  // (buttons/links/selects/textareas) here so their behavior isn't hijacked and
  // we never double-submit. Several selectors CAN be mounted at once (the Reports
  // page dialog plus one rerun dialog per open report window), so what keeps this
  // listener from colliding is the ownerDocument scoping below, not uniqueness:
  // each one hears only its own document's keys.
  useEffect(() => {
    if (!onSubmit || !selectedItem) return undefined;
    const doc = inputRef.current?.ownerDocument || document; // see the note above
    const onDocKeyDown = (e) => {
      if (e.key !== 'Enter' || e.nativeEvent?.isComposing) return;
      const t = e.target;
      if (t === inputRef.current) return; // the input resolves its own typed text
      if (t && typeof t.closest === 'function' && t.closest('button, a, select, textarea')) return;
      e.preventDefault();
      const sel = buildSelection(selectedItem, groupMode, specificAccount);
      onChange?.(sel);
      setShowDropdown(false);
      onSubmit(sel);
    };
    doc.addEventListener('keydown', onDocKeyDown);
    return () => doc.removeEventListener('keydown', onDocKeyDown);
  }, [onSubmit, selectedItem, groupMode, specificAccount, buildSelection, onChange]);

  const handleChange = (value) => {
    setPortfolio(value);
    setSelectedItem(null);
    setSpecificAccount('ALL');
    const { mode } = parseQuery(value);
    if (mode) setGroupMode(mode);
    setItems(value ? filterItems(data, value, acctGroupIndex) : buildItemsList(data));
    setShowDropdown(true);
    emit(null, mode || groupMode, 'ALL');
  };

  const handleSelect = (item) => {
    applySelection(item, item.type === 'group' ? groupMode : undefined);
  };

  // Enter resolves the typed shortname/account and generates via onSubmit. A
  // typed codename for a multi-account client (or no clean match) opens the
  // dropdown instead of guessing. IME composition is ignored.
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (e.nativeEvent?.isComposing) return;
    if (disabled || loading) return;
    e.preventDefault();
    if (!onSubmit) return;

    if (selectedItem) {
      const sel = buildSelection(selectedItem, groupMode, specificAccount);
      onChange?.(sel);
      setShowDropdown(false);
      onSubmit(sel);
      return;
    }

    const { item, mode, ambiguous } = resolveTypedSelection(data, portfolio, acctGroupIndex);
    if (ambiguous || !item) {
      setItems(portfolio ? filterItems(data, portfolio, acctGroupIndex) : buildItemsList(data));
      setShowDropdown(true);
      return;
    }
    const sel = applySelection(item, mode);
    onSubmit(sel);
  };

  const setMode = (mode) => {
    setGroupMode(mode);
    if (selectedItem && selectedItem.type === 'group') {
      setPortfolio(`${mode === 'aggregate' ? '@@' : '@'}${selectedItem.codename}`);
      emit(selectedItem, mode, specificAccount);
    }
  };

  const setSpecific = (acct) => {
    setSpecificAccount(acct);
    emit(selectedItem, groupMode, acct);
  };

  const availableAccounts = (selectedItem && selectedItem.type === 'group')
    ? (selectedItem.members || []).map((a) => ({ account_number: a }))
    : [];
  const { mode: typedMode } = parseQuery(portfolio);
  const isFallbackList = items.length > 0 && items.every((i) => i.viaAccount);
  const groupIntentNoHits = typedMode && items.length === 0;

  const sectionCls = `px-3 py-1 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'bg-[#141414] text-gray-400' : 'bg-gray-100 text-gray-500'}`;
  const itemCls = `w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-teal-500 hover:text-white ${darkMode ? 'text-gray-200' : 'text-gray-900'}`;

  // Groups, families and composites are all @-selectable containers and render
  // identically apart from the accent colour, so one row renderer serves all
  // three sections. `kind` (not `type`) splits them: a family/composite carries
  // type 'group' precisely so every selection/mode/narrowing path treats it as
  // one.
  const isGroupKind = (i) => i.type === 'group' && (i.kind || 'group') === 'group';
  const isFamilyKind = (i) => i.type === 'group' && i.kind === 'family';
  const isCompositeKind = (i) => i.type === 'group' && i.kind === 'composite';
  const renderContainerRow = (item, i, accent) => (
    <button
      key={`${accent}-${item.codename}-${i}`}
      type="button"
      onClick={() => handleSelect(item)}
      className={itemCls}
    >
      <Users className={`w-4 h-4 ${accent} shrink-0`} />
      <span className={`font-medium ${accent}`}>@{item.codename}</span>
      {item.name && <span className="text-sm opacity-70 truncate">— {item.name}</span>}
      <span className={`ml-auto text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
        {item.account_count ?? (item.members ? item.members.length : 0)}
      </span>
    </button>
  );

  return (
    <div>
      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative" ref={dropdownRef}>
        <input
          ref={inputRef}
          type="text"
          value={portfolio}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (!suppressFocusOpenRef.current) setShowDropdown(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Search accounts, or @group / @family / @composite  (@@ = aggregate)"
          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${darkMode ? 'bg-[#1a1a1a] border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
          disabled={disabled || loading}
        />
        <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'} pointer-events-none`} />
        {showDropdown && !disabled && (
          <div className={`absolute z-10 w-full mt-1 max-h-64 overflow-auto rounded-lg border shadow-lg ${darkMode ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-gray-200'}`}>
            {loading ? (
              <div className={`p-3 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</div>
            ) : groupIntentNoHits ? (
              <div className={`p-3 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No matching group, and no account by that name is in a group yet.</div>
            ) : items.length === 0 ? (
              <div className={`p-3 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No matching portfolios found</div>
            ) : (
              <>
                {isFallbackList && <div className={sectionCls}>Groups, families and composites containing that account</div>}
                {!isFallbackList && items.some(isGroupKind) && <div className={sectionCls}>Groups</div>}
                {items.filter(isGroupKind).map((item, i) => renderContainerRow(item, i, 'text-purple-500'))}
                {!isFallbackList && items.some(isFamilyKind) && <div className={sectionCls}>Families</div>}
                {items.filter(isFamilyKind).map((item, i) => renderContainerRow(item, i, 'text-amber-500'))}
                {!isFallbackList && items.some(isCompositeKind) && <div className={sectionCls}>Composites</div>}
                {items.filter(isCompositeKind).map((item, i) => renderContainerRow(item, i, 'text-sky-500'))}
                {!isFallbackList && items.some((i) => i.type === 'account') && <div className={sectionCls}>Accounts</div>}
                {items.filter((i) => i.type === 'account').map((item, i) => (
                  <button key={`a-${item.account_number}-${i}`} type="button" onClick={() => handleSelect(item)} className={itemCls}>
                    <User className="w-4 h-4 opacity-50 shrink-0" />
                    <span className="truncate">{item.name || item.account_number}</span>
                    <span className="ml-auto font-mono text-xs opacity-60">{item.account_number}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {selectedItem && selectedItem.type === 'group' && (
        <div className="mt-3">
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Group output</label>
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-white/10 text-sm">
            {[{ k: 'sequential', label: 'Per-account (@)' }, { k: 'aggregate', label: 'Aggregate (@@)' }].map((opt) => (
              <button key={opt.k} type="button" onClick={() => setMode(opt.k)} disabled={disabled}
                className={`flex-1 px-3 py-2 disabled:opacity-50 disabled:pointer-events-none ${groupMode === opt.k ? 'bg-teal-600 text-white' : darkMode ? 'bg-[#1a1a1a] text-gray-300' : 'bg-white text-gray-700'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedItem && selectedItem.type === 'group' && availableAccounts.length > 0 && (
        <div className="mt-3">
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Specific Account (Optional)</label>
          <select value={specificAccount} onChange={(e) => setSpecific(e.target.value)} disabled={disabled}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 disabled:opacity-50 ${darkMode ? 'bg-[#1a1a1a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
            <option value="ALL">ALL — Include all {availableAccounts.length} accounts</option>
            {availableAccounts.map((acc, idx) => (
              <option key={`${acc.account_number}-${idx}`} value={acc.account_number}>{acc.account_number}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default PortfolioSelector;
